import React, { useState, useEffect } from "react";
import {
  ArrowRight,
  ShieldCheck,
  CheckCircle2,
  X,
  Lock,
  ExternalLink,
  Mail,
  Infinity as InfinityIcon,
} from "lucide-react";
import { Link } from "react-router-dom";
import { markSubscribed, SUBSCRIBE_GATE_EVENT } from "../lib/subscription";
import { CHECKOUT_URLS, CHECKOUT_FALLBACK, SUPPORT_EMAIL } from "../lib/constants";

/**
 * WelcomeGate — subscription/plan-chooser modal.
 *
 * Opt-in only: opens when a CTA dispatches `eviq:open-welcome-gate`, or
 * when the user returns from a checkout with `?checkout=success` in the URL.
 *
 * Payment flow: Stripe hosted payment links (owner's Stripe account).
 * We collect the email first, create a pending activation record, then
 * open the Stripe link with prefilled_email + client_reference_id so the
 * /api/webhook/stripe-links webhook can auto-verify the payment. The gate
 * polls activation status and unlocks the app the moment payment lands.
 */
export const WelcomeGate = ({ forceOpen = false, onClose }) => {
  const [visible, setVisible] = useState(false);
  const [selected, setSelected] = useState("yearly"); // yearly = best value default
  const [step, setStep] = useState("choose"); // "choose" | "instructions"

  useEffect(() => {
    if (forceOpen) setVisible(true);
  }, [forceOpen]);

  // Listen for global open requests dispatched by any CTA on the page.
  useEffect(() => {
    const open = () => {
      setStep("choose");
      setVisible(true);
    };
    window.addEventListener(SUBSCRIBE_GATE_EVENT, open);
    return () => window.removeEventListener(SUBSCRIBE_GATE_EVENT, open);
  }, []);

  // Detect return-from-checkout so we can show the "next step" instructions.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("checkout") === "success") {
      setStep("instructions");
      setVisible(true);
      // Optimistically flag the user as subscribed — support will confirm.
      markSubscribed("pending", "yearly");
    }
  }, []);

  const dismiss = () => {
    setVisible(false);
    if (onClose) onClose();
  };

  const goToCheckout = () => {
    const url = CHECKOUT_URLS[selected];
    if (url) {
      // Collect the email first so the Stripe payment can be auto-matched
      // back to this account by the webhook.
      setStep("instructions");
      return;
    }
    window.location.href = CHECKOUT_FALLBACK;
    dismiss();
  };

  if (!visible) return null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-md overflow-y-auto"
      data-testid="welcome-gate"
      role="dialog"
      aria-modal="true"
      aria-labelledby="welcome-gate-title"
    >
      {/* Dismiss */}
      <button
        onClick={dismiss}
        data-testid="welcome-dismiss"
        aria-label="Close"
        className="absolute top-4 right-4 sm:top-6 sm:right-6 text-zinc-500 hover:text-white p-2 rounded-full border border-white/10 hover:border-white/25 transition-colors z-10"
      >
        <X className="w-4 h-4" strokeWidth={2} />
      </button>

      <div className="relative min-h-screen flex flex-col items-center justify-center px-5 sm:px-6 py-10 max-w-2xl mx-auto">
        {step === "instructions" ? (
          <StripeCheckoutFlow selected={selected} onClose={dismiss} />
        ) : (
          <>
            {/* Logo */}
            <div className="relative mb-6 sm:mb-8">
              <img
                src="/eviq-app-icon.png"
                alt="EVIQ"
                className="w-24 h-24 sm:w-28 sm:h-28 rounded-[24px] sm:rounded-[28px] shadow-[0_20px_80px_-20px_rgba(0,229,255,0.5)]"
              />
            </div>

            {/* Headline */}
            <div className="text-center mb-8">
              <div className="font-mono text-[10px] sm:text-xs tracking-[0.2em] uppercase text-zinc-500 mb-3">
                Choose your plan
              </div>
              <h1
                id="welcome-gate-title"
                className="font-display text-3xl sm:text-4xl text-white font-semibold leading-tight"
              >
                Unlock every{" "}
                <span className="brand-text-gradient">EVIQ feature.</span>
              </h1>
              <p className="mt-4 text-sm sm:text-base text-zinc-400 leading-relaxed" data-testid="welcome-gate-subhead">
                Choose from one of the three plan options — no contract, cancel anytime by email.
              </p>
            </div>

            {/* Plan cards */}
            <div
              className="w-full grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6"
              data-testid="plan-chooser"
            >
              <PlanCard
                active={selected === "basic"}
                onSelect={() => setSelected("basic")}
                name="Basic"
                price="$9.99"
                unit="/month"
                bullets={["1 vehicle", "Live SoH tracking", "Warranty alerts", "Basic anomaly detection"]}
                testid="plan-basic"
              />
              <PlanCard
                active={selected === "yearly"}
                onSelect={() => setSelected("yearly")}
                name="Yearly"
                price="$99.99"
                unit="/year"
                badge="Best value"
                bullets={["Everything in Basic", "Battery Life Predictor", "AI narrative + PDF", "Up to 3 vehicles"]}
                testid="plan-yearly"
              />
              <PlanCard
                active={selected === "lifetime"}
                onSelect={() => setSelected("lifetime")}
                name="Lifetime"
                price="$199.99"
                unit="one-time"
                badge="Pay once"
                bullets={["Everything in Yearly", "Marketplace value", "Cell health map", "Up to 5 vehicles"]}
                testid="plan-lifetime"
              />
            </div>

            {/* Primary CTA */}
            <button
              onClick={goToCheckout}
              data-testid="welcome-checkout"
              className="w-full brand-gradient text-black font-semibold rounded-2xl px-5 py-4 text-base inline-flex items-center justify-center gap-2 hover:opacity-90 transition-opacity glow-green mb-3"
            >
              {selected === "lifetime" && !CHECKOUT_URLS.lifetime ? (
                <>
                  Request lifetime access
                  <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
                </>
              ) : (
                <>
                  Continue to secure checkout
                  <ExternalLink className="w-4 h-4" strokeWidth={2.5} />
                </>
              )}
            </button>

            <p className="text-[11px] text-zinc-500 text-center leading-relaxed mb-6">
              {selected === "lifetime" && !CHECKOUT_URLS.lifetime
                ? "We'll set up lifetime access manually — takes one business day."
                : "Stripe secure checkout · unlocks automatically · Cancel anytime by email"}
            </p>

            {/* Trust row */}
            <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-[10px] font-mono uppercase tracking-wider text-zinc-500">
              <TrustBadge icon={ShieldCheck} label="Cancel anytime" />
              <span className="text-white/10">·</span>
              <TrustBadge icon={Lock} label="Encrypted checkout" />
              <span className="text-white/10">·</span>
              <TrustBadge icon={Mail} label="Email cancellation" />
            </div>

            {/* Alt link */}
            <button
              onClick={dismiss}
              data-testid="welcome-preview-link"
              className="mt-8 text-xs text-zinc-500 hover:text-white transition-colors underline-offset-2 hover:underline"
            >
              Keep browsing the preview →
            </button>
          </>
        )}
      </div>
    </div>
  );
};

// ---------- Sub-components ----------
const PlanCard = ({ active, onSelect, name, price, unit, badge, bullets, testid }) => (
  <button
    type="button"
    onClick={onSelect}
    data-testid={testid}
    aria-pressed={active}
    className={`relative text-left rounded-2xl border px-4 py-4 transition-all ${
      active
        ? "border-[#39FF14]/50 bg-white/[0.04] shadow-[0_0_0_1px_rgba(57,255,20,0.35),0_0_28px_-8px_rgba(57,255,20,0.4)]"
        : "border-white/10 bg-black/40 hover:border-white/25"
    }`}
  >
    <div className="flex items-center justify-between mb-2">
      <span className={`text-xs font-mono uppercase tracking-wider ${active ? "text-white" : "text-zinc-500"}`}>
        {name}
      </span>
      <span
        className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${
          active ? "border-[#39FF14] bg-[#39FF14]" : "border-white/25 bg-transparent"
        }`}
      >
        {active && <span className="w-1.5 h-1.5 rounded-full bg-black" />}
      </span>
    </div>
    <div className="flex items-baseline gap-1 mb-3">
      <span className={`text-2xl font-semibold ${active ? "text-white" : "text-zinc-300"}`}>
        {price}
      </span>
      <span className="font-mono text-[10px] text-zinc-500">{unit}</span>
    </div>
    <ul className="space-y-1.5">
      {bullets.map((b) => (
        <li key={b} className="flex items-start gap-2 text-[11px] text-zinc-400 leading-snug">
          <CheckCircle2 className="w-3 h-3 mt-0.5 shrink-0" style={{ color: "#39FF14" }} strokeWidth={2.5} />
          {b}
        </li>
      ))}
    </ul>
    {badge && (
      <span
        className="absolute -top-2 right-3 font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded text-black"
        style={{ background: "linear-gradient(135deg,#39FF14,#00E5FF)" }}
      >
        {badge}
      </span>
    )}
  </button>
);

const TrustBadge = ({ icon: Icon, label }) => (
  <span className="inline-flex items-center gap-1.5">
    <Icon className="w-3 h-3" strokeWidth={1.8} />
    {label}
  </span>
);

const StripeCheckoutFlow = ({ selected, onClose }) => {
  const planLabel =
    selected === "lifetime"
      ? "Lifetime · $199.99 one-time"
      : selected === "yearly"
      ? "Yearly · $99.99/year"
      : "Basic · $9.99/month";

  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [activationId, setActivationId] = useState(null); // waiting for payment
  const [unlocked, setUnlocked] = useState(false);

  const API = process.env.REACT_APP_BACKEND_URL || "";

  // Poll payment status while waiting — the /api/webhook/stripe-links
  // webhook flips the record to paid the moment Stripe confirms payment.
  useEffect(() => {
    if (!activationId || unlocked) return undefined;
    const id = setInterval(async () => {
      try {
        const r = await fetch(`${API}/api/subscribe/stripe-status/${activationId}`);
        if (!r.ok) return;
        const j = await r.json();
        if (j.paid) {
          markSubscribed("active", selected);
          setUnlocked(true);
        }
      } catch {
        /* transient poll error — keep trying */
      }
    }, 3500);
    return () => clearInterval(id);
  }, [activationId, unlocked, selected, API]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email || !/.+@.+\..+/.test(email)) {
      setError("Please enter a valid email address.");
      return;
    }
    setSubmitting(true);
    // Open a blank tab synchronously (inside the click) so popup blockers
    // don't eat the Stripe redirect after the async fetch resolves.
    const w = window.open("about:blank", "_blank");
    let refCode = null;
    try { refCode = window.localStorage.getItem("eviq.referral_code") || null; } catch { /* noop */ }
    try {
      const res = await fetch(`${API}/api/subscribe/stripe-start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase(), plan_id: selected, referral_code: refCode }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || "Could not start checkout — please try again.");
      }
      const data = await res.json();
      if (typeof window !== "undefined") {
        window.localStorage.setItem("eviq.subscription_email", email.trim().toLowerCase());
      }
      if (w) w.location = data.checkout_url;
      else window.open(data.checkout_url, "_blank", "noopener,noreferrer");
      setActivationId(data.activation_id);
    } catch (e2) {
      if (w) w.close();
      setError(e2.message || "Could not start checkout — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const manualUnlock = () => {
    // Fallback if the webhook confirmation is delayed — unlock provisionally;
    // the webhook (or the admin) still reconciles the payment record.
    markSubscribed("active", selected);
    setUnlocked(true);
  };

  if (unlocked) {
    return (
      <div className="text-center" data-testid="welcome-activated">
        <img
          src="/eviq-app-icon.png"
          alt="EVIQ"
          className="w-20 h-20 rounded-[20px] mx-auto mb-6 shadow-[0_20px_80px_-20px_rgba(57,255,20,0.5)]"
        />
        <div
          className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-5"
          style={{ background: "rgba(57,255,20,0.14)", border: "2px solid rgba(57,255,20,0.45)" }}
        >
          <CheckCircle2 className="w-7 h-7" style={{ color: "#39FF14" }} strokeWidth={2.5} />
        </div>
        <h2 className="font-display text-2xl sm:text-3xl text-white font-semibold">
          Your account is <span className="brand-text-gradient">unlocked.</span>
        </h2>
        <p className="text-zinc-400 text-sm sm:text-base mt-3 leading-relaxed max-w-md mx-auto">
          Welcome to EVIQ — every feature of <span className="text-white font-medium">{planLabel}</span> is
          now available in the app.
        </p>
        <a
          href="/app"
          data-testid="welcome-activated-open-app"
          className="mt-8 brand-gradient text-black font-semibold rounded-full px-6 py-3 text-sm inline-flex items-center gap-2 hover:opacity-90 transition-opacity"
        >
          Open EVIQ
          <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
        </a>
      </div>
    );
  }

  if (activationId) {
    return (
      <div className="text-center w-full" data-testid="welcome-waiting-payment">
        <img
          src="/eviq-app-icon.png"
          alt="EVIQ"
          className="w-20 h-20 rounded-[20px] mx-auto mb-6 shadow-[0_20px_80px_-20px_rgba(0,229,255,0.5)]"
        />
        <div
          className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-5"
          style={{ background: "rgba(0,229,255,0.10)", border: "2px solid rgba(0,229,255,0.4)" }}
        >
          <span
            className="w-6 h-6 rounded-full border-2 border-[#00E5FF] border-t-transparent animate-spin"
            aria-hidden="true"
          />
        </div>
        <h2 className="font-display text-2xl sm:text-3xl text-white font-semibold">
          Complete your payment in the <span className="brand-text-gradient">Stripe tab.</span>
        </h2>
        <p className="text-zinc-400 text-sm sm:text-base mt-3 leading-relaxed max-w-md mx-auto">
          We opened <span className="text-white font-medium">{planLabel}</span> checkout in a new tab.
          The moment Stripe confirms your payment, this screen unlocks automatically — no
          waiting on support.
        </p>
        <div className="mt-6 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider text-zinc-500">
          <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: "#00E5FF" }} />
          Watching for payment confirmation…
        </div>
        <div className="mt-8">
          <button
            type="button"
            onClick={manualUnlock}
            data-testid="welcome-manual-unlock"
            className="text-xs text-zinc-500 hover:text-white transition-colors underline underline-offset-2"
          >
            I've completed payment — unlock now
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="text-center w-full" data-testid="welcome-instructions">
      <img
        src="/eviq-app-icon.png"
        alt="EVIQ"
        className="w-20 h-20 rounded-[20px] mx-auto mb-6 shadow-[0_20px_80px_-20px_rgba(57,255,20,0.5)]"
      />
      <h2 className="font-display text-2xl sm:text-3xl text-white font-semibold">
        One step to <span className="brand-text-gradient">secure checkout.</span>
      </h2>
      <p className="text-zinc-400 text-sm sm:text-base mt-3 leading-relaxed max-w-md mx-auto">
        Tell us the email for your <span className="text-white font-medium">{planLabel}</span> account,
        then pay on Stripe. Your account unlocks automatically the moment payment completes.
      </p>

      <form onSubmit={submit} className="mt-6 text-left space-y-3 max-w-md mx-auto">
        <label className="block">
          <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
            Your email
          </span>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            data-testid="activate-email"
            autoComplete="email"
            disabled={submitting}
            className="mt-1 w-full rounded-lg border border-white/10 bg-black/60 px-3 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[#39FF14]/50 focus:ring-1 focus:ring-[#39FF14]/40 disabled:opacity-50"
          />
        </label>

        {error && (
          <div
            data-testid="activate-error"
            className="rounded-lg border border-[#FF6B57]/30 bg-[#FF6B57]/[0.08] px-3 py-2 text-xs text-[#FF6B57]"
          >
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting || !email}
          data-testid="activate-submit"
          className="w-full brand-gradient text-black font-semibold rounded-full px-6 py-3 text-sm inline-flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? "Opening Stripe…" : "Continue to Stripe checkout"}
          {!submitting && <ExternalLink className="w-4 h-4" strokeWidth={2.5} />}
        </button>

        <p className="text-[11px] text-zinc-500 leading-relaxed text-center pt-1">
          Payments are processed by Stripe — cards, Apple Pay and Google Pay accepted.
          Your account activates automatically when the payment confirms.
        </p>
      </form>
    </div>
  );
};
